import { useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Card, CardContent, Chip, Divider, Fade, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography, } from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";

import MenuFrame from "../frames/menu_frame";
import HtmlTooltip from "../frames/html_tooltip";
import Loading1 from "../frames/loading1";
import { getGstJson } from "./finops_halper";

const getMonthList = (startDateStr) => {
    const [startMonth, startYear] = startDateStr.split("-").map(Number);

    const today = new Date();
    const endMonth = today.getMonth();
    const endYear = today.getFullYear();

    const result = [];

    if (startYear > endYear || (startYear === endYear && startMonth - 1 > endMonth)) {
        return result;
    }

    let currentYear = startYear;
    let currentMonth = startMonth - 1;

    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
        result.push(`${String(currentMonth + 1).padStart(2, "0")}-${currentYear}`);
        currentMonth++;
        if (currentMonth > 11) {
            currentMonth = 0;
            currentYear++;
        }
    }
    return result.reverse();
};

function FinOps() {
    const [isLoading, setIsLoading] = useState(false);
    const [gstData, setGstData] = useState(["Select a month"]);
    const [selectedMonth, setSelectedMonth] = useState(null);

    const months = useMemo(() => getMonthList("04-2025"), []);

    const initData = async (auth) => {
        if (!auth) return;
    };

    const monthChange = (_, value) => {
        if (!value || isLoading) return;
        setSelectedMonth(value);
        setIsLoading(true);
        getGstJson(value).then((res) => {
            setGstData(res);
        }).catch((err) => {
            console.error(err);
            setGstData([]);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(gstData, null, 4));
    };

    return (
        <MenuFrame permission="ORG_ADMIN" screen="FINOPS" onInit={initData}>
            <Stack spacing={3} sx={{ py: 2 }}>
                <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, }} >
                    <CardContent>
                        <Stack direction={{ xs: "column", md: "row" }} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" } }} spacing={3} >
                            <Box>
                                <Typography variant="h5" fontWeight={700}>GST Report</Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Select a month to fetch GST JSON data.</Typography>
                            </Box>

                            <Autocomplete
                                sx={{ minWidth: { xs: "100%", sm: 300, }, }}
                                value={selectedMonth}
                                options={months}
                                onChange={monthChange}
                                disableClearable
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Reporting Month"
                                        placeholder="Select month"
                                        fullWidth
                                        InputProps={{
                                            ...params.slotProps.input,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <CalendarMonthRoundedIcon color="primary" fontSize="small" />
                                                    </InputAdornment>
                                                    {params.slotProps.input?.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, }} >
                    <CardContent>
                        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                <ReceiptLongRoundedIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>GST JSON</Typography>
                                {selectedMonth && (<Chip size="small" color="primary" label={selectedMonth} />)}
                            </Stack>
                            <HtmlTooltip title={<Typography variant="body2"> Copy JSON </Typography>} >
                                <span>
                                    <Tooltip title="Copy JSON">
                                        <IconButton color="primary" disabled={isLoading} onClick={copyJson}>
                                            <ContentCopyRoundedIcon />
                                        </IconButton>
                                    </Tooltip>
                                </span>
                            </HtmlTooltip>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ minHeight: 320, maxHeight: 420, overflow: "auto", bgcolor: "primary.background", borderRadius: 2, border: "1px solid", borderColor: "divider", p: 2, fontFamily: "monospace", }} >
                            {isLoading ? (
                                <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}><Loading1 /></Box>
                            ) : (
                                <Fade in>
                                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, }} >
                                        {JSON.stringify(gstData, null, 2)}
                                    </pre>
                                </Fade>
                            )}
                        </Box>
                    </CardContent>
                </Card>
                <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, }} >
                    <CardContent>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <MailOutlineRoundedIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Report Mail</Typography>
                        </Stack>
                        <Alert severity="info" sx={{ mt: 2 }} >Get report mail (Invoices, Receivable, Summary)</Alert>
                    </CardContent>
                </Card>
                <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, }} >
                    <CardContent>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <CheckCircleRoundedIcon color="success" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>GST Filing Status</Typography>
                        </Stack>
                        <Alert severity="success" sx={{ mt: 2 }} >GST filing status will be displayed here.</Alert>
                    </CardContent>
                </Card>
            </Stack>
        </MenuFrame>
    );
}

export default FinOps;
